import axios from 'axios';
import { getApiBaseUrl } from './utils/apiConfig';

// Create axios instance without baseURL initially
// We'll set it dynamically in the interceptor
const api = axios.create();

// Interceptor to set baseURL dynamically from config
api.interceptors.request.use(async (config) => {
  // Get the base URL (will load config if not already loaded)
  const baseURL = await getApiBaseUrl();
  config.baseURL = baseURL;
  
  // Add token to requests
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (username, inviterUsername, inviteDigits) =>
    api.post('/auth/register', { username, inviterUsername, inviteDigits }),
  getMe: () => api.get('/auth/me'),
  loginWithCode: (code) => api.post('/auth/login-with-code', { code }),
  shareLogin: () => api.post('/auth/share-login'),
  previewShareLogin: (shareToken) => api.post('/auth/preview-share-login', { shareToken }),
  redeemShareLogin: (shareToken) => api.post('/auth/redeem-share-login', { shareToken }),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  updateUsername: (userId, username) => api.put(`/users/${userId}/username`, { username }),
  uploadProfilePicture: async (userId, file) => {
    const formData = new FormData();
    formData.append('profilePicture', file);
    
    const token = localStorage.getItem('token');
    const baseURL = await getApiBaseUrl();
    return axios.post(`${baseURL}/users/${userId}/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    });
  },
  removeProfilePicture: (userId) => api.delete(`/users/${userId}/profile-picture`),
  deleteUsers: (userIds) => api.delete('/users', { data: { userIds } }),
};

export const invitesAPI = {
  preview: (inviterUsername, inviteDigits) =>
    api.get('/invites/preview', { params: { inviterUsername, inviteDigits } }),
};

export const teamsAPI = {
  getAll: () => api.get('/teams'),
  create: (teamName) => api.post('/teams', { teamName }),
  generateInvite: (teamId) => api.post(`/teams/${teamId}/invite`),
  inviteUserWithCode: (teamId, username) => api.post(`/teams/${teamId}/invite-user-code`, { username }),
  join: (inviterUsername, inviteDigits) =>
    api.post('/teams/join', { inviterUsername, inviteDigits }),
  leave: (teamId) => api.post(`/teams/${teamId}/leave`),
};

export const contactsAPI = {
  generateInvite: () => api.post('/contacts/invite'),
  join: (inviterUsername, inviteDigits) =>
    api.post('/contacts/join', { inviterUsername, inviteDigits }),
};

export const messagesAPI = {
  getMessages: (conversationId, type = 'direct') => 
    api.get('/messages', { params: { conversationId, type } }),
  deleteMessage: (messageId) => api.delete(`/messages/${messageId}`),
};

export const filesAPI = {
  uploadFile: async (file, conversationId, type = 'direct', onUploadProgress = null) => {
    const uploadStartTime = Date.now();
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
    console.log(`[UPLOAD] Starting upload: ${file.name}, size: ${fileSizeMB}MB`);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('conversationId', conversationId);
    formData.append('type', type);
    
    const token = localStorage.getItem('token');
    const baseURL = await getApiBaseUrl();
    
    let lastProgressLog = 0;
    return axios.post(`${baseURL}/files/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      },
      timeout: 0,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      onUploadProgress: onUploadProgress ? (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        const elapsed = Date.now() - uploadStartTime;
        const speed = progressEvent.loaded / (elapsed / 1000); // bytes per second
        const speedMBps = (speed / 1024 / 1024).toFixed(2);
        
        // Log progress every 10%
        if (percentCompleted - lastProgressLog >= 10) {
          console.log(`[UPLOAD] Progress: ${percentCompleted}% (${(progressEvent.loaded / 1024 / 1024).toFixed(2)}MB/${fileSizeMB}MB), speed: ${speedMBps}MB/s, elapsed: ${(elapsed / 1000).toFixed(1)}s`);
          lastProgressLog = percentCompleted;
        }
        
        onUploadProgress(percentCompleted);
      } : undefined
    }).then(response => {
      const totalTime = Date.now() - uploadStartTime;
      console.log(`[UPLOAD] Upload completed successfully in ${(totalTime / 1000).toFixed(2)}s`);
      return response;
    }).catch(error => {
      const totalTime = Date.now() - uploadStartTime;
      console.error(`[UPLOAD] Upload failed after ${(totalTime / 1000).toFixed(2)}s:`, error);
      throw error;
    });
  },
};

export const userFilesAPI = {
  getFolders: (sharedBy = null) =>
    api.get('/user-files/folders', sharedBy ? { params: { sharedBy } } : undefined),
  getSharedContacts: () => api.get('/user-files/shared-contacts'),
  getFolderSharing: (folderId) => api.get(`/user-files/folders/${folderId}/sharing`),
  updateFolderSharing: (folderId, userIds, writeUserIds = []) =>
    api.put(`/user-files/folders/${folderId}/sharing`, { userIds, writeUserIds }),
  getFolderPublicShare: (folderId) => api.get(`/user-files/folders/${folderId}/public-share`),
  setFolderPublicShare: (folderId, enabled) =>
    api.put(`/user-files/folders/${folderId}/public-share`, { enabled }),
  createFolder: (name, parentId = null) =>
    api.post('/user-files/folders', { name, parentId }),
  renameFolder: (folderId, name) => api.put(`/user-files/folders/${folderId}`, { name }),
  moveFolder: (folderId, parentId) =>
    api.put(`/user-files/folders/${folderId}`, { parentId }),
  deleteFolder: (folderId) => api.delete(`/user-files/folders/${folderId}`),
  getFiles: (folderId, { includeSubfolders } = {}) =>
    api.get(
      `/user-files/folders/${folderId}/files`,
      includeSubfolders ? { params: { subfolders: 1 } } : undefined
    ),
  reorderFiles: (folderId, storedNames) =>
    api.put(`/user-files/folders/${folderId}/files/order`, { storedNames }),
  moveFiles: (folderId, targetFolderId, storedNames) =>
    api.post(`/user-files/folders/${folderId}/files/move`, { targetFolderId, storedNames }),
  deleteFile: (folderId, storedName) =>
    api.delete(`/user-files/folders/${folderId}/files/${encodeURIComponent(storedName)}`),
  uploadFile: async (folderId, file, onUploadProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const baseURL = await getApiBaseUrl();
    return axios.post(`${baseURL}/user-files/folders/${folderId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
      timeout: 0,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      onUploadProgress: onUploadProgress
        ? (progressEvent) => {
            const total = progressEvent.total || progressEvent.loaded || 1;
            const percent = Math.round((progressEvent.loaded * 100) / total);
            onUploadProgress(percent);
          }
        : undefined,
    });
  },
};

// Publicly shared folders. These endpoints need no login — the share token in
// the URL is the credential — so they use a bare axios instance.
const publicApi = axios.create();
publicApi.interceptors.request.use(async (config) => {
  config.baseURL = await getApiBaseUrl();
  return config;
});

export const publicFilesAPI = {
  getFolderTree: (shareToken) => publicApi.get(`/public/folders/${shareToken}`),
  getFiles: (shareToken, folderId, { includeSubfolders } = {}) =>
    publicApi.get(
      `/public/folders/${shareToken}/folders/${folderId}/files`,
      includeSubfolders ? { params: { subfolders: 1 } } : undefined
    ),
  fileUrl: async (shareToken, folderId, storedName, { download = false } = {}) => {
    const baseURL = (await getApiBaseUrl()).replace(/\/$/, '');
    const url = `${baseURL}/public/folders/${shareToken}/folders/${folderId}/files/${encodeURIComponent(storedName)}`;
    return download ? `${url}?download=1` : url;
  },
};

export const serverAPI = {
  shutdown: () => api.post('/server/shutdown'),
  resetDatabase: () => api.post('/server/reset-database'),
};

export const groupsAPI = {
  create: (name, userIds) => api.post('/groups', { name, userIds }),
  getAll: () => api.get('/groups'),
  getById: (groupId) => api.get(`/groups/${groupId}`),
  hide: (groupId) => api.post(`/groups/${groupId}/hide`),
  unhide: (groupId) => api.post(`/groups/${groupId}/unhide`),
};

export default api;

